import React from 'react';
import { Link } from 'react-router-dom';

/**
 * System & Process Demos — a launcher of guided, self-contained demos an
 * operator can run for a prospective customer. Each demo walks a real-world
 * problem through the platform's modules using mock data (no real tenants).
 */

interface Demo {
  slug: string;
  icon: string;
  title: string;
  scenario: string;
  modules: string[];
  duration: string;
  available: boolean;
}

const DEMOS: Demo[] = [
  {
    slug: 'birthday-seating',
    icon: '🎂',
    title: '250-Guest Birthday Seating',
    scenario:
      'A host sent 300 invites through a third party and got 250 RSVPs. They need to seat all 250 under specific rules — and want the lowest-cost way to use the platform.',
    modules: ['RSVP intake', 'Seating rules', 'Auto-seat', 'Cost options'],
    duration: '~3 min',
    available: true,
  },
  {
    slug: 'checkin-day-of',
    icon: '✅',
    title: 'Day-Of Check-In at the Door',
    scenario: 'Walk a prospect through QR check-in, packet pickup, and live gate counts on event day.',
    modules: ['Check-In', 'QR cards', 'Gate counts'],
    duration: '~2 min',
    available: false,
  },
  {
    slug: 'order-import',
    icon: '📥',
    title: 'Order Import & Reconciliation',
    scenario: 'Show how a third-party order file becomes guest records with ticket numbers, de-duplicated and reconciled.',
    modules: ['Ticketing', 'Import', 'Reconciliation'],
    duration: '~2 min',
    available: false,
  },
];

export function PlatformDemosPage() {
  return (
    <div>
      <div style={styles.headingRow}>
        <div>
          <h1 style={styles.h1}>System &amp; Process Demos</h1>
          <p style={styles.sub}>
            Launch a guided walkthrough of the platform for a prospective customer. Each demo uses
            sample data — nothing touches a real tenant.
          </p>
        </div>
      </div>

      <div style={styles.grid}>
        {DEMOS.map((d) => (
          <div key={d.slug} style={{ ...styles.card, ...(d.available ? null : styles.cardSoon) }}>
            <div style={styles.cardTop}>
              <span style={styles.icon}>{d.icon}</span>
              {d.available ? (
                <span style={styles.duration}>{d.duration}</span>
              ) : (
                <span style={styles.soonBadge}>Coming soon</span>
              )}
            </div>
            <div style={styles.title}>{d.title}</div>
            <div style={styles.scenario}>{d.scenario}</div>
            <div style={styles.modules}>
              {d.modules.map((m) => (
                <span key={m} style={styles.chip}>{m}</span>
              ))}
            </div>
            {d.available ? (
              <Link to={`/platform/demos/${d.slug}`} style={styles.launchBtn}>
                ▶ Launch demo
              </Link>
            ) : (
              <span style={styles.launchDisabled}>▶ Launch demo</span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

const ACCENT = '#4f46e5';
const BORDER = '#e5e7eb';
const MUTED = '#6b7280';
const INK = '#0b1120';

const styles: Record<string, React.CSSProperties> = {
  headingRow: { marginBottom: 18 },
  h1: { fontSize: '1.6rem', fontWeight: 800, margin: 0, color: INK },
  sub: { color: MUTED, fontSize: '0.9rem', margin: '4px 0 0', maxWidth: 640 },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 16 },
  card: { background: '#fff', border: `1px solid ${BORDER}`, borderRadius: 14, padding: 18, boxShadow: '0 1px 3px rgba(16,24,40,0.04)', display: 'flex', flexDirection: 'column' },
  cardSoon: { opacity: 0.7 },
  cardTop: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 },
  icon: { fontSize: '2rem' },
  duration: { fontSize: '0.72rem', fontWeight: 700, color: ACCENT, background: '#eef2ff', borderRadius: 999, padding: '2px 10px' },
  soonBadge: { fontSize: '0.7rem', fontWeight: 700, color: '#64748b', background: '#f1f5f9', borderRadius: 999, padding: '2px 10px' },
  title: { fontWeight: 800, fontSize: '1.05rem', color: INK },
  scenario: { color: MUTED, fontSize: '0.84rem', marginTop: 6, lineHeight: 1.5, flex: 1 },
  modules: { display: 'flex', flexWrap: 'wrap', gap: 6, margin: '14px 0' },
  chip: { background: '#eef2ff', color: ACCENT, borderRadius: 6, padding: '2px 8px', fontSize: '0.72rem', fontWeight: 700 },
  launchBtn: { background: ACCENT, color: '#fff', borderRadius: 8, padding: '10px 0', fontSize: '0.85rem', fontWeight: 700, textDecoration: 'none', textAlign: 'center' },
  launchDisabled: { background: '#e2e8f0', color: '#94a3b8', borderRadius: 8, padding: '10px 0', fontSize: '0.85rem', fontWeight: 700, textAlign: 'center', cursor: 'not-allowed' },
};
